import moment from 'moment-timezone';
import { metricKeys } from '@pulse/metrics';
import { CDN_ACCOUNT_TYPE, PRODUCT_MODE_MAP } from '@utils/constants';
import { formatEndedStatus } from '@utils/humanize';
import { getMetricDisplayName } from '@utils/metrics';
import getTimeInHMSFormat from '@utils/getTimeInHMSFormat';
import alphanumericSort from '@src/utils/alphanumericSort';

const DateFormatterStyle = 'HH:mm:ss, MMM DD YYYY';

/* eslint max-len:0 */
export const columnsMetrics = {
  Mandatory: ['SessionId', 'ViewerId', 'SessionStartTime'],
  'Basic Information': [
    'tags.m3.dv.osf',
    'ISP',
    'Ip',
    'IpHash',
    'Ipv6',
    'Ipv6Hash',
    'ASN',
    'AssetName',
    'tags.channel',
    'ContentLengthSecs',
    'tags.c3.video.isLive',
  ],
  'Startup Metrics': [
    'LifeJoinTimeSecs',
    'ExitBeforeVideoStart',
    'LifeJoinedAvgBitrateKbps',
    'VideoStartFailure',
    'VideoStartFailureBusiness',
    'VideoStartFailureTech',
    'VideoPlaybackFailure',
    'VideoPlaybackFailureBusiness',
    'VideoPlaybackFailureTech',
    'VideoStartFailureErrorCode',
    'VideoStartFailureBusinessErrorCode',
    'VideoStartFailureTechErrorCode',
    'VideoPlaybackFailureErrorCode',
    'VideoPlaybackFailureBusinessErrorCode',
    'VideoPlaybackFailureTechErrorCode',
  ],
  'Lifetime Playback Metrics': [
    'LifePlaytimeSecs',
    'PercentageComplete',
    'LifeRebufferingRatio',
    'LifeNetworkRebufferingRatio',
    'LifeBufferingTimeSecs',
    'LifeAvgBitrateKbps',
    'LifeAverageAvgBitrateKbps',
    'LifeAvgFramesPerSecs',
    'LifeVideoRestartTimeSecs',
    'EndedStatus',
    'LifeLastPlayHeadTimeMs',
    'LifeNumBitrateSwitches',
    'LifeExitRelatedCIRTimeMs',
    'IsExitDueToCIR',
    'LifeEncodedFrames',
    'LifeRenderedFrames',
    'LifeDropFrameRatio',
    // 'LifeCDNs',
    // 'LifeResources',
  ],
  'Interval Playback Metrics': [
    // 'IntervalPlaytimeSecs',
    'IntervalRebufferingTimeSecs',
    'IntervalNetworkRebufferingTimeSecs',
    'IntervalAvgBitrateKbps',
    'IntervalAverageAvgBitrateKbps',
    // 'IntervalAvgFramesPerSec',
    // 'IntervalVideoRestartTimeSecs',
    'IntervalCDN',
    // 'IntervalResource',
  ],
};

const isNumeric = (num) => {
  return !isNaN(num);
};

const formatMilliSecs = (data) => {
  if (isNumeric(data)) {
    if (data < 0) {
      return `${data}s`;
    }
    const milliseconds = Number(data);
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    seconds = seconds % 60;
    minutes = minutes % 60;
    return getTimeInHMSFormat(hours, minutes, seconds);
  } else {
    return '-';
  }
};

const formatSecs = (data) => {
  if (isNumeric(data)) {
    let seconds = Number(data);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    seconds = seconds % 60;
    minutes = minutes % 60;
    return getTimeInHMSFormat(hours, minutes, seconds);
  } else {
    return '-';
  }
};

export const columnValueFormatter = (v, type) => {
  let value;
  switch (type) {
    case 'SessionStartTime':
      value = moment(v).format(DateFormatterStyle);
      break;
    case 'LifeJoinTimeSecs':
    case 'LifeBufferingTimeSecs':
    case 'LifeVideoRestartTimeSecs':
    case 'IntervalPlaytimeSecs':
    case 'IntervalRebufferingTimeSecs':
    case 'IntervalNetworkRebufferingTimeSecs':
    case 'IntervalVideoRestartTimeSecs':
      value = `${Number(v).toFixed(1)}s`;
      break;
    case 'EndedStatus':
      value = formatEndedStatus(v);
      break;
    case 'LifeRebufferingRatio':
    case 'PercentageComplete':
    case 'LifeNetworkRebufferingRatio':
    case 'LifeDropFrameRatio':
      value = `${Number(v).toFixed(1)}%`;
      break;
    case 'LifeAvgBitrateKbps':
    case 'LifeAverageAvgBitrateKbps':
    case 'IntervalAvgBitrateKbps':
    case 'IntervalAverageAvgBitrateKbps':
    case 'LifeJoinedAvgBitrateKbps':
      value = `${Number(v).toFixed(1)}Kbps`;
      break;
    case 'LifeAvgFramesPerSecs':
      value = `${Number(v).toFixed(1)}FPS`;
      break;
    case 'ContentLengthSecs':
    case 'LifePlaytimeSecs':
      value = formatSecs(v);
      break;
    case 'LifeLastPlayHeadTimeMs':
    case 'LifeExitRelatedCIRTimeMs':
      value = formatMilliSecs(v);
      break;
    default:
      value = v;
      break;
  }
  return value;
};

export const defaultMetricColumns = (
  metric,
  timelineEnabled = false,
  customerAccountType = null,
) => {
  const metricsColumns = {
    [metricKeys.VideoStartFailures]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoStartFailureErrorCode',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.VideoStartFailuresBusiness]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoStartFailureBusinessErrorCode',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.VideoStartFailuresTech]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoStartFailureTechErrorCode',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.VideoPlaybackFailures]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoPlaybackFailureErrorCode',
      'LifePlaytimeSecs',
      'LifeRebufferingRatio',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.VideoPlaybackFailuresBusiness]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoPlaybackFailureBusinessErrorCode',
      'LifePlaytimeSecs',
      'LifeRebufferingRatio',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.VideoPlaybackFailuresTech]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'VideoPlaybackFailureTechErrorCode',
      'LifePlaytimeSecs',
      'LifeRebufferingRatio',
      'tags.m3.dv.osf',
      'AssetName',
      'IntervalCDN',
      'tags.channel',
      'ISP',
      'ASN',
    ],
    [metricKeys.ExitBeforeVideoStarts]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ISP',
      'ASN',
    ],
    [metricKeys.RebufferingRatio]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeRebufferingRatio',
      'LifeBufferingTimeSecs',
      // 'IntervalRebufferingTimeSecs',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.ConnectionInducedRebufferingRatio]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeNetworkRebufferingRatio',
      // 'IntervalNetworkRebufferingTimeSecs',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.Bitrate]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeAvgBitrateKbps',
      'LifeAverageAvgBitrateKbps',
      //  'IntervalAvgBitrateKbps',
      //  'IntervalAverageAvgBitrateKbps',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.Framerate]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeAvgFramesPerSecs',
      // 'IntervalAvgFramesPerSec',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.VideoStartTime]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.VideoRestartTime]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeVideoRestartTimeSecs',
      // 'IntervalVideoRestartTimeSecs',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.AvgBitrate]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeVideoRestartTimeSecs',
      // 'IntervalVideoRestartTimeSecs',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
      'ASN',
      'ISP',
    ],
    [metricKeys.ExitDueToCIR]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeVideoRestartTimeSecs',
      // 'IntervalVideoRestartTimeSecs',
      'LifeExitRelatedCIRTimeMs',
      'IsExitDueToCIR',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
    ],
    [metricKeys.LifeDropFrameRatio]: [
      'SessionId',
      'ViewerId',
      'SessionStartTime',
      'LifeJoinTimeSecs',
      'LifePlaytimeSecs',
      'LifeVideoRestartTimeSecs',
      // 'IntervalVideoRestartTimeSecs',
      'LifeEncodedFrames',
      'LifeRenderedFrames',
      'LifeDropFrameRatio',
      'AssetName',
      'tags.channel',
      'tags.m3.dv.osf',
      'IntervalCDN',
    ],
  };
  if (!metricsColumns[metric]) return [];
  let updatedMetricColumns: any = metricsColumns[metric].filter((v) =>
    timelineEnabled ? true : v !== 'SessionId',
  );
  updatedMetricColumns = updatedMetricColumns.filter((item) => {
    if (item !== 'IntervalCDN' || customerAccountType !== CDN_ACCOUNT_TYPE) {
      return item;
    }
  });
  return updatedMetricColumns;
};

export const getColumnsNameReflection = () => ({
  SessionId: 'Session ID',
  ViewerId: 'Viewer ID',
  SessionStartTime: 'Session Start Time',
  'tags.m3.dv.osf': 'Device Operating System',
  ISP: 'ISP',
  Ip: 'Ipv4',
  IpHash: 'Ipv4Hash',
  Ipv6: 'Ipv6',
  Ipv6Hash: 'Ipv6Hash',
  ASN: 'ASN',
  AssetName: 'Asset Name',
  'tags.channel': 'Channel Name',
  ContentLengthSecs: 'Content Length',
  'tags.c3.video.isLive': 'Content Category',
  LifePlaytimeSecs: 'Playing Time',
  PercentageComplete: 'Average % Complete',
  LifeEncodedFrames: 'Encoded Frames',
  LifeRenderedFrames: 'Rendered Frames',
  LifeDropFrameRatio: 'Dropped Frames Ratio',
  LifeResources: 'Resources',
  LifeBufferingTimeSecs: 'Rebuffering Time',
  IntervalPlaytimeSecs: 'Playing Time for the minute',
  LifeLastPlayHeadTimeMs: 'Last Playhead Time',
  IntervalRebufferingTimeSecs: 'Rebuffering Time for the selected minute',
  IntervalNetworkRebufferingTimeSecs: 'Connection Induced Rebuffering Time for the selected minute',
  IntervalCDN: 'CDN',
  IntervalResource: 'Resource for the minute',
  Status: 'Status',
  Attempts: 'Attempts',
  EndedStatus: 'Ended Status',
  IsExitDueToCIR: 'CIR Related Exits',
  LifeExitRelatedCIRTimeMs: 'CIR Time preceding Exit',
  LifeRebufferingRatio: getMetricDisplayName({
    metricKey: 'RebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  StreamingPerformanceIndex: getMetricDisplayName({
    metricKey: 'StreamingPerformanceIndex',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeJoinTimeSecs: getMetricDisplayName({
    metricKey: 'VideoStartTime',
    context: PRODUCT_MODE_MAP.EI,
  }),
  ExitBeforeVideoStart: getMetricDisplayName({
    metricKey: 'ExitBeforeVideoStarts',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailure: getMetricDisplayName({
    metricKey: 'VideoStartFailures',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureBusiness: getMetricDisplayName({
    metricKey: 'VideoStartFailuresBusiness',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureTech: getMetricDisplayName({
    metricKey: 'VideoStartFailuresTech',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailure: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailures',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailureBusiness: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailuresBusiness',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailureTech: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailuresTech',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailures', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoStartFailureBusinessErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailuresBusiness', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoStartFailureTechErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailuresTech', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailures', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureBusinessErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresBusiness', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureTechErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresTech', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  LifeAvgBitrateKbps: getMetricDisplayName({ metricKey: 'Bitrate', context: PRODUCT_MODE_MAP.EI }),
  LifeAverageAvgBitrateKbps: getMetricDisplayName({
    metricKey: 'AvgBitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeAvgFramesPerSecs: getMetricDisplayName({
    metricKey: 'Framerate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeVideoRestartTimeSecs: `Total ${getMetricDisplayName({ metricKey: 'VideoRestartTime', context: PRODUCT_MODE_MAP.EI })}`,
  LifeNetworkRebufferingRatio: getMetricDisplayName({
    metricKey: 'ConnectionInducedRebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifePausedTimeSecs: getMetricDisplayName({
    metricKey: 'LifePausedTimeMins',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifePausedTimePct: getMetricDisplayName({
    metricKey: 'LifePausedTimePct',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeNumBitrateSwitches: getMetricDisplayName({
    metricKey: 'LifeAvgNumBitrateSwitches',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeJoinedAvgBitrateKbps: getMetricDisplayName({
    metricKey: 'LifeJoinedAvgBitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  IntervalAverageAvgBitrateKbps: `${getMetricDisplayName({ metricKey: 'AvgBitrate', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
  IntervalAvgBitrateKbps: `${getMetricDisplayName({ metricKey: 'Bitrate', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
  IntervalAvgFramesPerSec: `${getMetricDisplayName({ metricKey: 'Framerate', context: PRODUCT_MODE_MAP.EI })} for the minute`,
  IntervalVideoRestartTimeSecs: `${getMetricDisplayName({ metricKey: 'VideoRestartTime', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
});

const formatStyle = 'HHH DD, HH:MM';

export const momentTime = (value) => moment(value).format(formatStyle);

export const sortAndSearchTable = (key, searchText, sort, data, orderKey) => {
  let finalData = data;
  const re = new RegExp(searchText, 'i');
  // Use key to search text in data
  if (key) {
    // key can be tags$m3$dv$os which is actually tags.m3.dv.os
    const keyFixed = key.replace(/\$/g, '.');
    finalData = finalData.filter((item) => item[keyFixed] && re.test(item[keyFixed]));
  }
  // Use orderKey to sort existing data
  if (orderKey) {
    finalData = finalData.sort((prev, next) =>
      alphanumericSort(sort, prev[orderKey], next[orderKey]) ? 1 : -1,
    );
  }

  return finalData;
};

export const MAX_ANCHORED_COUNT = 3;

export const isDrilldownFeatureByMetric = (metric) =>
  [
    metricKeys.VideoStartFailures,
    metricKeys.VideoStartFailuresBusiness,
    metricKeys.VideoStartFailuresTech,
    metricKeys.VideoPlaybackFailures,
    metricKeys.VideoPlaybackFailuresBusiness,
    metricKeys.VideoPlaybackFailuresTech,
    metricKeys.ExitBeforeVideoStarts,
    metricKeys.RebufferingRatio,
    metricKeys.ConnectionInducedRebufferingRatio,
    metricKeys.Bitrate,
    metricKeys.Framerate,
    metricKeys.VideoStartTime,
    metricKeys.VideoRestartTime,
    metricKeys.AvgBitrate,
    metricKeys.ExitDueToCIR,
    metricKeys.LifeDropFrameRatio,
  ].includes(metric);

export const transformOldMetrics = (metrics) => {
  return metrics;
};

// Timeline Api needs these 3 columns data to fetch response, so when we request scan api(viewer session table),
// we must make sure that the below columns we request
export const FIXED_REQUIRED_COLUMNS = ['SessionId', 'ClientId', 'SessionStartTime'];

export const GROUPBY_MAPPING = {
  GEO_CITY: 'geo.city',
  GEO_COUNTRY: 'geo.country',
  GEO_DMA: 'geo.dma',
  GEO_ISP: 'ISP',
  GEO_ASN: 'ASN',
  GEO_CONTINENT: 'geo.continent',
  CDN: 'IntervalCDN',
  GEO_STATE: 'geo.state',
  GEO_POSTAL_CODE: 'geo.postalCode',
  OBJECT_NAME: 'AssetName',
  LIVE_VIDEO: 'tags.c3.video.isLive',
  RESOURCE_ID: 'IntervalResource',
};

const replaceKey = (key) => key.replace(/\./g, '$');

export const replaceFinalDataKey = (finalData) =>
  finalData.map((data) =>
    Object.fromEntries(Object.keys(data).map((key) => [replaceKey(key), data[key]])),
  );

export const replaceColumnDataKey = (columns) =>
  columns.map((column) => ({ ...column, dataKey: replaceKey(column.dataKey) }));

export const scanMetricFilters = {
  [metricKeys.VideoStartFailures]: {
    type: 'equal',
    dimension: 'IS_VIDEO_START_FAILURE',
    value: true,
  },
  [metricKeys.VideoPlaybackFailures]: {
    type: 'equal',
    dimension: 'IS_VIDEO_PLAYBACK_FAILURE',
    value: true,
  },
  [metricKeys.VideoStartFailuresBusiness]: {
    type: 'equal',
    dimension: 'IS_VIDEO_START_FAILURE_BUSINESS',
    value: true,
  },
  [metricKeys.VideoStartFailuresTech]: {
    type: 'equal',
    dimension: 'IS_VIDEO_START_FAILURE_TECH',
    value: true,
  },
  [metricKeys.VideoPlaybackFailuresBusiness]: {
    type: 'equal',
    dimension: 'IS_VIDEO_PLAYBACK_FAILURE_BUSINESS',
    value: true,
  },
  [metricKeys.VideoPlaybackFailuresTech]: {
    type: 'equal',
    dimension: 'IS_VIDEO_PLAYBACK_FAILURE_TECH',
    value: true,
  },
  [metricKeys.ExitDueToCIR]: {
    type: 'equal',
    dimension: 'IS_EXIT_DUE_TO_CIR',
    value: true,
  },
  [metricKeys.ExitBeforeVideoStarts]: {
    type: 'equal',
    dimension: 'ExitBeforeVideoStart',
    value: true,
  },
  // add JUST_JOINED clause to focus only on the sessions the joined in the selected timeframe
  [metricKeys.VideoStartTime]: {
    type: 'equal',
    dimension: 'JUST_JOINED',
    value: true,
  },
};
